import {
  Box,
  useToast
} from "@chakra-ui/react";
import {
  isAfter,
  subSeconds
} from "date-fns";
import Peer from "peerjs";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import styled from "styled-components";

import {
  Conversation,
  Topic,
  User
} from "src/api";
import { useAuthContext } from "src/hooks/useAuthContext";
import { useSocket } from "src/hooks/useSocket";

import { UserAudio } from "./AudioBox";
import { SessionTimer } from "./SessionTimer";

interface ChatProps {
  conversation: Conversation;
  topic: Topic;
  userId: string;
  isSessionOver: boolean;
  mediaStream: MediaStream | null;
}

interface Partner extends User {
  connected: boolean;
}

export const VoiceChat: React.FC<ChatProps> = ({
  topic,
  isSessionOver,
  conversation,
  mediaStream
}) => {
  const toast = useToast();
  const { authDataState } = useAuthContext();
  const [ socket ] = useSocket(topic._id);
  // Refs
  const peerInstance = useRef<Peer | null>(null);
  // State
  const [ myStream, setMyStream ] = useState<MediaStream | null>(null);
  const [ partner, setPartner ] = useState<Partner | null>(null);
  const [ showPartner, setShowPartner ] = useState(false);
  const [ myAnalyser, setMyAnalyser ] = useState<AnalyserNode>();
  const [ partnerAnalyser, setPartnerAnalyser ] = useState<AnalyserNode>();
  const [ audioDisabled, setAudioIsDisabled ] = useState(false);
  const [ muted, setMuted ] = useState(false);

  /**
   * Handles how we interpret a partner's audio stream
   */
  const addPartnerAudioStream = useCallback((stream: MediaStream) => {
    // Create a new audio context
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Create an element to play the audio
    const audio = new Audio();

    // Set the src of the audio to the stream
    audio.srcObject = stream;
    // Setup the max delay allowed in seconds
    const delayNode = ctx.createDelay(Math.max(conversation.latencySeconds, 0.00001));

    // Setup the delay value in seconds (we need both)
    delayNode.delayTime.value = conversation.latencySeconds;

    // When data has loaded
    audio.onloadedmetadata = function () {
      if (audio) {
        // Create a media stream source using the Audio srcObject
        const source = ctx.createMediaStreamSource(audio.srcObject as MediaStream);
        const analyser = ctx.createAnalyser();

        source.connect(delayNode);
        source.connect(analyser);
        setPartnerAnalyser(analyser);
        delayNode.connect(ctx.destination);
      }
    };
  }, [ conversation.latencySeconds ]);

  /**
   * Connect to a user's Peer instance using their userId.
   */
  const connectToNewUser = useCallback((userId: string, stream: MediaStream) => {
    // Call the user by their ID, providing our stream for when they answer

    const call = peerInstance.current?.call(userId, stream);

    // If they answer, setup their stream:
    call?.on("stream", userVideoStream => {
      addPartnerAudioStream(userVideoStream);
    });
  }, [ addPartnerAudioStream ]);

  /**
   * Toggle our stream, allowing mute/unmute
   *
   */
  const toggleMyStream = useCallback(() => {
    if (myStream && !audioDisabled) {
      const changeTo = !myStream.getAudioTracks()[ 0 ].enabled;

      myStream.getAudioTracks()[ 0 ].enabled = changeTo;
      setMuted(!changeTo);
    }
  }, [ myStream, audioDisabled ]);

  useEffect(() => {
    // Use the media stream from parent (which handles recording)
    if (!mediaStream || isSessionOver) {
      return;
    }

    try {
      // Add to our audio stream for visualisation
      setMyStream(mediaStream);

      const audio = new Audio();

      audio.srcObject = mediaStream;
      audio.muted = true;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaStreamSource(audio.srcObject);
      const analyser = ctx.createAnalyser();

      source.connect(analyser);
      setMyAnalyser(analyser);
    } catch (error) {
      console.error(error);

      toast({
        status: "error",
        title: "There was a problem setting up your audio."
      });
    }
  }, [
    toast,
    isSessionOver,
    mediaStream
  ]);

  const respondToCall = useCallback((call: Peer.MediaConnection) => {
    // Answer with our stream
    if (myStream) {
      call.answer(myStream);
    }

    // When we receive their stream:
    call?.on("stream", userVideoStream => {
      addPartnerAudioStream(userVideoStream);
    });
  }, [ addPartnerAudioStream, myStream ]);

  useEffect(() => {
    if (authDataState.user?._id && !isSessionOver) {
      // Other peers can connect to this peer using the provided ID.
      // If no ID is given, one will be generated by the brokering server.
      const peerId = authDataState.user._id;

      if (!peerInstance.current) {
        // A peer can connect to other peers and listen for connections.
        peerInstance.current = new Peer(peerId, {
          // Connection details for the brokering server
          path: "/peerjs",
          host: process.env.REACT_APP_PEER_HOST || "d37qd058irkhh7.cloudfront.net",
          // Log level: 1 (errors), 2 (+ warnings), 3 (+ info)
          debug: 2,
          secure: !!process.env.REACT_APP_PEER_SECURE || true
        });
      }

      peerInstance.current.on("error", _error => {
        console.error("Peer error", _error);

        toast({
          status: "error",
          title: "There was an error setting up the connection"
        });
      });
      // When we receive a call:
      peerInstance.current.on("call", call => respondToCall(call));
    }
  }, [
    myStream,
    addPartnerAudioStream,
    authDataState.user,
    toast,
    respondToCall,
    socket?.connected,
    isSessionOver
  ]);

  useEffect(() => {
    if (myStream) {
      if (partner && !partner?.connected) {
        connectToNewUser(partner._id, myStream);
      }
    }
  }, [
    myStream,
    partner,
    connectToNewUser
  ]);

  useEffect(() => {
    // When somebody new connects
    socket?.on("topic:participants", (users: User[]) => {
      // In V1 we are only using 1 partner.
      // This partner may have been removed, so first set null
      setPartner(null);
      setShowPartner(false);

      // dedupe users
      const dedupedUsers = users.filter((user, index, self) =>
        index === self.findIndex(t => t._id === user._id)
      );

      // Then for each connected user
      dedupedUsers.forEach(user => {
        // Filter out our own user

        if (user._id !== authDataState.user?._id) {
          // In V1 we are only using 1 partner, so can just set a single partner
          setPartner({
            ...user,
            connected: false
          });

          // Then wait latencySeconds to add the partner to the UI
          setTimeout(() => {
            setShowPartner(true);
          }, (conversation.latencySeconds + 0.5) * 1000);
        }
      });
    });
  }, [
    authDataState.user?._id,
    connectToNewUser,
    conversation.latencySeconds,
    socket
  ]);

  /**
   * Handle when the session comes to an end
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (myStream && isAfter(new Date(), subSeconds(new Date(conversation.endTime), conversation.latencySeconds))) {
        setAudioIsDisabled(true);
        // Disconnect with the peer
        peerInstance.current?.disconnect();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [ conversation, myStream ]);

  return (
    <PageContainer>
      {!isSessionOver && (
        <SessionTimer
          conversation={conversation}
          partner={showPartner ? partner : null}
        />
      )}

      {!isSessionOver && (
        <AudioContainer>
          {(partnerAnalyser && partner) && (
            <UserAudio
              audioDisabled={audioDisabled}
              hidden={!showPartner}
              name={partner.name}
              analyser={partnerAnalyser}
              delayMs={conversation.latencySeconds * 1000}
            />
          )}

          {authDataState.user && (
            <UserAudio
              audioDisabled={audioDisabled}
              name={authDataState.user.name}
              analyser={myAnalyser}
              delayMs={0}
              byCurrentUser
              onMute={toggleMyStream}
              muted={muted}
            />
          )}
        </AudioContainer>
      )}
    </PageContainer>
  );
};

export const AudioContainer = styled(Box)`
  margin-top: 1rem;
  padding: 2rem 1rem;
  border-radius: 10px;
  background-color: #fafafa;
  position: relative;
  flex: 1;
`;

export const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
`;
